import { RATE_THRESHOLD } from "./constants"
import PlanSelector from "./PlanSelector"
import { Color } from "../../../../constants/V2/color"
import SliderInput from "../../../elements/V2/SliderInput"
import Typography from "../../../elements/V2/Typography"
import LayoutItem from "../../Layouts/LayoutItem"

import TextButton from "@components/elements/V2/Buttons/TextButton"
import NumberDisplay from "@components/elements/V2/NumberDisplay"
import { ADJUSTED_PLANS_SLIDER } from "@constants/segmentEvents"
import { trackSegmentEvent } from "@utils/analytics"
import { formatPrice } from "@utils/price"
import { getAccentColor } from "@utils/V2/color"
import React, { useState, useRef } from "react"

export type PlanData = {
  title: string
  fromAmount: string
  payoutDescription?: string
  standardAmount: number
  reducedAmount: number
}

type Props = {
  title: string
  description: string
  minRange: number
  maxRange: number
  initialSliderValue: number
  backgroundColor: Color
  onSliderChange?: (value: number) => void
  mobileGridOrder?: number
  plans: PlanData[]
  recommendedPlanTransactionAmount?: string
}

const CalcRecommended = ({
  title,
  description,
  minRange,
  maxRange,
  initialSliderValue,
  backgroundColor,
  onSliderChange,
  mobileGridOrder,
  plans,
  recommendedPlanTransactionAmount,
  ...props
}: Props) => {
  const hasTrackedFirstSlide = useRef(false)

  const [rangeValue, setRangeValue] = useState(
    initialSliderValue > maxRange
      ? maxRange
      : Math.max(initialSliderValue, minRange)
  )

  const plansWithNumericFees = plans.filter((plan) => {
    return !isNaN(plan.standardAmount)
  })

  const [selectedPlan, setSelectedPlan] = useState(
    plansWithNumericFees[0]?.title || ""
  )

  const selectedPlanData = plansWithNumericFees.find(
    (plan) => plan.title === selectedPlan
  )

  const recommendedPlanFromAmount = recommendedPlanTransactionAmount ?? ""
  const isRecommendedPlanFeeNumeric =
    recommendedPlanFromAmount !== "" &&
    !isNaN(Number.parseFloat(recommendedPlanFromAmount))

  const handleRangeSlider = (value: number) => {
    if (!hasTrackedFirstSlide.current) {
      trackSegmentEvent(ADJUSTED_PLANS_SLIDER)
      hasTrackedFirstSlide.current = true
    }
    setRangeValue(value)
    if (onSliderChange) {
      onSliderChange(value)
    }
  }

  const payAmount = selectedPlanData
    ? (
        ((rangeValue <= RATE_THRESHOLD
          ? selectedPlanData.standardAmount
          : selectedPlanData.reducedAmount) /
          100) *
        rangeValue
      ).toFixed(2)
    : undefined

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      className="col-span-full mt-24 flex w-full max-w-[652px] flex-col gap-32 sm:col-span-2 md:col-span-4 lg:col-span-8 lg:ml-60"
      {...props}
    >
      <div className="flex flex-col gap-24 md:gap-32 lg:gap-40">
        <div className="mb-4">
          <Typography
            font="grotesk"
            weight="medium"
            size="h3"
            color={getAccentColor(backgroundColor)}
            text={title}
            element="h3"
          />
          <Typography
            font="grotesk"
            weight="book"
            size="body-md"
            color={getAccentColor(backgroundColor)}
            text={description}
            className="opacity-70"
          />
        </div>

        <PlanSelector
          plans={plansWithNumericFees.map((plan) => plan.title)}
          selectedPlan={selectedPlan}
          onSelect={setSelectedPlan}
        />

        <div className="lg:max-w-[618px]">
          <SliderInput
            steps={1}
            size="large"
            labelText="Monthly sales"
            value={rangeValue}
            minValue={minRange}
            maxValue={maxRange}
            textColor={getAccentColor(backgroundColor)}
            onChange={handleRangeSlider}
          />
        </div>

        <div className="mt-[28px] flex w-full flex-col gap-y-10 tabular-nums md:flex-row md:gap-x-80">
          <div className="flex flex-col">
            <Typography
              font="grotesk"
              weight="medium"
              size="body-md"
              color={getAccentColor(backgroundColor)}
              text="Transaction fees"
            />
            {selectedPlanData ? (
              <>
                {isRecommendedPlanFeeNumeric ? (
                  <>
                    <NumberDisplay
                      type="percentage"
                      size="matchFont"
                      valueFontSize="lead-lg"
                      value={
                        rangeValue <= RATE_THRESHOLD
                          ? selectedPlanData.standardAmount
                          : selectedPlanData.reducedAmount
                      }
                      adornmentText="%"
                      textColor={getAccentColor(backgroundColor)}
                      adornmentTextPosition="right"
                      sideScriptText="excl. VAT"
                    />
                    <Typography
                      font="grotesk"
                      weight="book"
                      size="body-sm"
                      color={getAccentColor(backgroundColor)}
                      text="On local debit card transactions"
                      className="hidden opacity-70 sm:block"
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      font="grotesk"
                      weight="medium"
                      size="lead-lg"
                      color={getAccentColor(backgroundColor)}
                      text="Custom"
                    />
                  </>
                )}
              </>
            ) : (
              <div className="flex flex-col">
                <Typography
                  font="grotesk"
                  weight="medium"
                  size="lead-lg"
                  color={getAccentColor(backgroundColor)}
                  text="-"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <Typography
              font="grotesk"
              weight="medium"
              size="body-md"
              color={getAccentColor(backgroundColor)}
              text="You'll pay"
            />
            {selectedPlanData ? (
              <>
                {isRecommendedPlanFeeNumeric ? (
                  <>
                    <NumberDisplay
                      type="price"
                      size="matchFont"
                      valueFontSize="lead-lg"
                      value={
                        payAmount
                          ? formatPrice(payAmount, {
                              shouldShowDecimals: true,
                              hasThousandsSeparator: true,
                            })
                          : "-"
                      }
                      adornmentText="R"
                      textColor={getAccentColor(backgroundColor)}
                      adornmentTextPosition="left"
                      sideScriptText="excl. VAT"
                    />
                    <Typography
                      font="grotesk"
                      weight="book"
                      size="body-sm"
                      color={getAccentColor(backgroundColor)}
                      text={
                        selectedPlanData?.payoutDescription || "Standard payout"
                      }
                      className="opacity-70"
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      font="grotesk"
                      weight="medium"
                      size="lead-lg"
                      color={getAccentColor(backgroundColor)}
                      text="Custom"
                    />
                    <div className="flex flex-col gap-10 md:flex-row">
                      <Typography
                        size="body-sm"
                        font="grotesk"
                        weight="book"
                        color={getAccentColor(backgroundColor)}
                        text="Talk to our sales team about custom rates"
                        className="opacity-70"
                      />
                      <TextButton
                        textSize="subscript-sm"
                        style="text"
                        weight="book"
                        text="click here"
                        color={Color.Blue}
                        linkUrl={"#intercom"}
                        className="mt-[2px]"
                      />
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex flex-col">
                <Typography
                  font="grotesk"
                  weight="medium"
                  size="lead-lg"
                  color={getAccentColor(backgroundColor)}
                  text="-"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutItem>
  )
}

export default CalcRecommended
